import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Check } from "lucide-react";
import { Dialog } from "@/components/ui/Dialog";
import { Alert } from "@/components/ui/Alert";
import { isStaleSessionError } from "@/lib/api/client";
import { StaleSessionNotice } from "@/features/account/components/StaleSessionNotice";
import { useCreateApiKey } from "../hooks/useApiKeys";
import type { ApiKeyScope } from "../api/api-keys.api";

const AVAILABLE_SCOPES: ApiKeyScope[] = ["deploy", "read", "apps"];

interface CreateApiKeyDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CreateApiKeyDialog({ open, onClose }: CreateApiKeyDialogProps) {
  const { t } = useTranslation();
  const createKey = useCreateApiKey();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<ApiKeyScope[]>(["deploy", "read"]);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyGen = useRef(0);
  const secretWarningId = useId();

  const secret = createKey.data?.key;
  const staleSession = isStaleSessionError(createKey.error);

  const toggleScope = (scope: ApiKeyScope) => {
    if (createKey.isPending) return;
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (createKey.isPending) return;
    if (!name.trim() || scopes.length === 0) return;
    createKey.mutate({ name: name.trim(), scopes });
  };

  const handleClose = () => {
    if (!createKey.isPending) {
      setName("");
      setScopes(["deploy", "read"]);
      setCopied(false);
      setCopyFailed(false);
      copyGen.current += 1;
      if (copyTimer.current) clearTimeout(copyTimer.current);
      createKey.reset();
      onClose();
    }
  };

  const copySecret = async () => {
    if (!secret) return;
    if (copyTimer.current) clearTimeout(copyTimer.current);
    const gen = copyGen.current;
    try {
      await navigator.clipboard.writeText(secret);
      if (gen !== copyGen.current) return;
      setCopyFailed(false);
      setCopied(true);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      if (gen !== copyGen.current) return;
      setCopied(false);
      setCopyFailed(true);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} title={t("apiKeys.createKey")}>
      {secret ? (
        <div className="space-y-4">
          <Alert id={secretWarningId} variant="warning">
            {t("apiKeys.secretWarning")}
          </Alert>
          <div className="flex items-center gap-2 rounded-lg bg-elevated p-3">
            <code className="flex-1 break-all font-mono text-xs text-heading">{secret}</code>
            <button
              type="button"
              onClick={copySecret}
              autoFocus
              aria-label={copied ? t("common.copied") : t("common.copy")}
              aria-describedby={secretWarningId}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted hover:bg-surface hover:text-heading"
            >
              {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
            </button>
          </div>
          {copyFailed && (
            <p role="alert" className="text-xs text-danger">
              {t("common.copyFailed")}
            </p>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="w-full rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright"
          >
            {t("common.confirm")}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="key-name" className="text-sm font-medium text-subtle">
              {t("apiKeys.name")}
            </label>
            <input
              id="key-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("apiKeys.namePlaceholder")}
              data-autofocus={staleSession ? undefined : true}
              readOnly={createKey.isPending}
              aria-disabled={createKey.isPending || undefined}
              className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo aria-disabled:opacity-50 hover:border-border-light"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-subtle">{t("apiKeys.scopes")}</span>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_SCOPES.map((scope) => (
                <button
                  key={scope}
                  type="button"
                  onClick={() => toggleScope(scope)}
                  aria-disabled={createKey.isPending || undefined}
                  className={`
                    rounded-lg px-3 py-1.5 text-xs font-medium transition-colors
                    aria-disabled:opacity-50 aria-disabled:cursor-not-allowed
                    ${
                      scopes.includes(scope)
                        ? "bg-indigo/10 text-indigo border border-indigo/30"
                        : "bg-elevated text-muted border border-border hover:border-border-light"
                    }
                  `}
                >
                  {scope}
                </button>
              ))}
            </div>
          </div>

          {createKey.error && !staleSession && (
            <p role="alert" className="text-xs text-danger">
              {createKey.error.error ?? t("common.error")}
            </p>
          )}
          {staleSession && (
            <StaleSessionNotice
              returnTo="/api-keys"
              body={t("apiKeys.staleSessionBody")}
              autoFocus
            />
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              aria-disabled={createKey.isPending || undefined}
              className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={
                !createKey.isPending && (!name.trim() || scopes.length === 0 || staleSession)
              }
              aria-disabled={createKey.isPending || undefined}
              aria-busy={createKey.isPending || undefined}
              className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              {createKey.isPending ? t("apiKeys.creating") : t("apiKeys.createKey")}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
